import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import ReportCarousel from './ReportCarousel';

function HistoryDetail({ analysisId, onBack }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalysis = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/api/history/${analysisId}`);
        if (response.data.success) {
          setAnalysis(response.data.data);
        } else {
          setError(response.data.error || 'Failed to load analysis');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load analysis');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [analysisId]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDecisionChip = (decision) => {
    if (!decision) return null;
    const colorMap = {
      BUY: { color: 'success' },
      SELL: { color: 'error' },
      HOLD: { color: 'warning' },
      OUTPERFORM: { sx: { bgcolor: '#81c784', color: '#1b5e20' } },
      UNDERPERFORM: { sx: { bgcolor: '#ef9a9a', color: '#b71c1c' } },
    };
    const config = colorMap[decision] || { color: 'default' };
    return (
      <Chip
        label={decision}
        color={config.color}
        sx={{ fontWeight: 'bold', ml: 2, ...config.sx }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 2 }}
          color="primary"
        >
          Back to History
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!analysis) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 2 }}
          color="primary"
        >
          Back to History
        </Button>
        <Alert severity="warning">Analysis not found</Alert>
      </Box>
    );
  }

  const reports = analysis.reports || {};

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          color="primary"
          variant="outlined"
        >
          Back to History
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'background.paper' }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography
            variant="h4"
            component={RouterLink}
            to={`/stock/${analysis.ticker}`}
            color="secondary"
            sx={{ fontWeight: 'bold', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {analysis.ticker}
          </Typography>
          {getDecisionChip(analysis.final_decision)}
        </Box>
        
        <Box display="flex" gap={4} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="textSecondary">
              Analysis Date
            </Typography>
            <Typography variant="body1">
              {analysis.analysis_date || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Created At
            </Typography>
            <Typography variant="body1">
              {formatDate(analysis.created_at)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Status
            </Typography>
            <Box>
              <Chip
                label={analysis.status}
                color={analysis.status === 'completed' ? 'success' : 'default'}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <ReportCarousel analysisData={reports} title="Analysis Reports" />

      {analysis.messages && analysis.messages.length > 0 && (
        <Accordion sx={{ mt: 3, backgroundColor: 'background.paper' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" color="primary">
              Activity Log ({analysis.messages.length} entries)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense sx={{ maxHeight: 500, overflow: 'auto' }}>
              {analysis.messages.map((msg, index) => {
                const typeColors = {
                  System: 'info.main',
                  Debate: 'secondary.main',
                  Error: 'error.main',
                  'Tool Call': 'warning.main',
                  Reasoning: 'text.secondary',
                  Data: 'text.secondary',
                  Input: 'text.secondary',
                  Summary: 'success.main',
                };
                const chipColor = msg.type === 'Error' ? 'error' : msg.type === 'System' ? 'info' : msg.type === 'Debate' ? 'secondary' : msg.type === 'Summary' ? 'success' : 'default';

                if (msg.type === 'Summary') {
                  return (
                    <ListItem key={index} sx={{ py: 1, alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={
                          <Box>
                            <Box display="flex" gap={1} alignItems="baseline" mb={0.5}>
                              <Typography variant="caption" color="textSecondary" sx={{ minWidth: 60, flexShrink: 0 }}>
                                {msg.timestamp}
                              </Typography>
                              <Chip label="Summary" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            </Box>
                            <Paper sx={{ p: 1.5, ml: 8.5, backgroundColor: 'rgba(76, 175, 80, 0.08)', border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                              <Typography variant="body2" sx={{ color: 'success.main', whiteSpace: 'pre-line', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {msg.content}
                              </Typography>
                            </Paper>
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                }

                return (
                  <ListItem key={index} sx={{ py: 0.25, alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Box display="flex" gap={1} alignItems="baseline">
                          <Typography variant="caption" color="textSecondary" sx={{ minWidth: 60, flexShrink: 0 }}>
                            {msg.timestamp}
                          </Typography>
                          <Chip
                            label={msg.type}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              flexShrink: 0,
                            }}
                            color={chipColor}
                            variant="outlined"
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: typeColors[msg.type] || 'text.primary',
                              wordBreak: 'break-word',
                            }}
                          >
                            {msg.content}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

export default HistoryDetail;
